/**
 * Tiny persistence helper for the "live doc → page" approval conversion intent.
 *
 * Why this lives in @confluence/approvals
 * ---------------------------------------
 * The intent expresses a fact about an approval lifecycle: "the user wanted to start an
 * approval but the source was a live doc, so we asked them to convert first". Approvals consumes
 * the intent after redirect, and @confluence/publish-modal sets it only after the user confirms
 * the convert-to-page publish path. Both @confluence/content-state and @confluence/publish-modal
 * already depend on @confluence/approvals, so colocating the helper here adds zero new package
 * edges and avoids the circular dependency that PR #377980 ran into when content-state tried to
 * imperatively call into publish-modal.
 *
 * Why sessionStorage
 * ------------------
 * The convert-to-page publish flow causes a full navigation to the newly-published page, so
 * any in-memory React state is lost. We need the intent to survive that single redirect so
 * the destination page's ApprovalsProvider can auto-open the approvals pane. sessionStorage
 * is scoped to the tab, so stale intents naturally expire when the tab closes.
 *
 * Why an in-memory mirror
 * -----------------------
 * When the `confluence_browser_storage_controls` gate is on, the underlying storage instance
 * delegates writes to `AtlBrowserStorageSession.setItem`, which is async — it awaits an
 * IndexedDB consent check before persisting. A synchronous read immediately after a write
 * can therefore return null. The in-memory cache provides a synchronous read for the same
 * tab; sessionStorage is consulted only on first read after a fresh page load (i.e. after
 * the redirect, when the in-memory variable has been reset).
 *
 * Feature-gate kill switch
 * ------------------------
 * The setter is the only entry point that writes anything. Callers MUST gate the setter behind
 * `expValEquals('confluence_approvals_eap', 'isEnabled', true)` before calling. With the gate off, nothing is ever set, so
 * the auto-open effects in `ApprovalsProvider` and `PublishModalMountPoint` (which also
 * gate-first) are no-ops and the feature is fully dormant.
 */
import { confluenceSessionStorageInstance as sessionStorageManager } from '@confluence/storage-manager/entry-points/storageInstance.ts';

export const LIVE_DOC_APPROVAL_REDIRECT_KEY = 'confluence.approvals.liveDoc.redirect';

export type LiveDocApprovalRedirectIntent = {
	contentId: string;
	createdAt: number;
};

/** Intents older than this are discarded on read if the publish flow never completed cleanly. */
const INTENT_TTL_MS = 30 * 60 * 1000;

let inMemoryRedirectIntent: LiveDocApprovalRedirectIntent | null = null;

export const setLiveDocApprovalRedirectIntent = ({ contentId }: { contentId: string }): void => {
	const intent: LiveDocApprovalRedirectIntent = {
		contentId,
		createdAt: Date.now(),
	};
	inMemoryRedirectIntent = intent;
	sessionStorageManager.setItem(LIVE_DOC_APPROVAL_REDIRECT_KEY, JSON.stringify(intent));
};

const isIntentExpired = (intent: LiveDocApprovalRedirectIntent): boolean =>
	Date.now() - intent.createdAt > INTENT_TTL_MS;

export const getLiveDocApprovalRedirectIntent = (): LiveDocApprovalRedirectIntent | null => {
	if (inMemoryRedirectIntent) {
		if (isIntentExpired(inMemoryRedirectIntent)) {
			clearLiveDocApprovalRedirectIntent();
			return null;
		}
		return inMemoryRedirectIntent;
	}

	const storedValue = sessionStorageManager.getItem(LIVE_DOC_APPROVAL_REDIRECT_KEY);
	if (!storedValue) {
		return null;
	}

	try {
		const parsed = JSON.parse(storedValue) as Partial<LiveDocApprovalRedirectIntent>;
		if (typeof parsed.contentId !== 'string' || typeof parsed.createdAt !== 'number') {
			return null;
		}

		const intent: LiveDocApprovalRedirectIntent = {
			contentId: parsed.contentId,
			createdAt: parsed.createdAt,
		};

		if (isIntentExpired(intent)) {
			clearLiveDocApprovalRedirectIntent();
			return null;
		}

		inMemoryRedirectIntent = intent;
		return intent;
	} catch {
		return null;
	}
};

export const clearLiveDocApprovalRedirectIntent = (): void => {
	inMemoryRedirectIntent = null;
	sessionStorageManager.removeItem(LIVE_DOC_APPROVAL_REDIRECT_KEY);
};

export const consumeLiveDocApprovalRedirectIntent = (): LiveDocApprovalRedirectIntent | null => {
	const intent = getLiveDocApprovalRedirectIntent();
	if (intent) {
		clearLiveDocApprovalRedirectIntent();
	}
	return intent;
};
